import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';

export interface SalaryCycleConfig {
  salaryStartDate: number; // Day of month (1-31)
  lastCycleEndDate?: Date; // Last cycle end date
  currentCycleStartDate?: Date; // Current cycle start date
  currentCycleEndDate?: Date; // Current cycle end date
}

export interface SalaryCycle {
  startDate: Date;
  endDate: Date;
  cycleKey: string; // Format: YYYY-MM-DD (start date)
}

/**
 * Get salary cycle configuration from company settings
 */
export const getSalaryCycleConfig = async (companyId?: string): Promise<SalaryCycleConfig | null> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  const configRef = doc(db, 'companies', uid, 'settings', 'salaryCycle');
  const configDoc = await getDoc(configRef);

  if (!configDoc.exists()) {
    return null;
  }

  const data = configDoc.data();
  return {
    salaryStartDate: data.salaryStartDate || 1,
    lastCycleEndDate: data.lastCycleEndDate?.toDate ? data.lastCycleEndDate.toDate() : (data.lastCycleEndDate ? new Date(data.lastCycleEndDate) : undefined),
    currentCycleStartDate: data.currentCycleStartDate?.toDate ? data.currentCycleStartDate.toDate() : (data.currentCycleStartDate ? new Date(data.currentCycleStartDate) : undefined),
    currentCycleEndDate: data.currentCycleEndDate?.toDate ? data.currentCycleEndDate.toDate() : (data.currentCycleEndDate ? new Date(data.currentCycleEndDate) : undefined),
  };
};

/**
 * Save salary cycle configuration
 */
export const saveSalaryCycleConfig = async (
  salaryStartDate: number,
  companyId?: string
): Promise<void> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  // Calculate current cycle dates
  const now = new Date();
  const currentCycle = calculateCurrentCycle(now, salaryStartDate);

  const configRef = doc(db, 'companies', uid, 'settings', 'salaryCycle');
  await setDoc(configRef, {
    salaryStartDate,
    currentCycleStartDate: currentCycle.startDate,
    currentCycleEndDate: currentCycle.endDate,
    updatedAt: new Date(),
  }, { merge: true });
};

/**
 * Calculate current salary cycle based on start date
 */
export const calculateCurrentCycle = (date: Date, salaryStartDate: number): SalaryCycle => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  let cycleStartYear = year;
  let cycleStartMonth = month;
  let cycleStartDay = salaryStartDate;

  // If current date is before the start date this month, cycle started last month
  if (day < salaryStartDate) {
    cycleStartMonth = month - 1;
    if (cycleStartMonth === 0) {
      cycleStartMonth = 12;
      cycleStartYear = year - 1;
    }
  }

  // Calculate cycle end date (day before next cycle starts)
  let cycleEndYear = cycleStartYear;
  let cycleEndMonth = cycleStartMonth + 1;
  let cycleEndDay = salaryStartDate - 1;

  if (cycleEndMonth > 12) {
    cycleEndMonth = 1;
    cycleEndYear = cycleStartYear + 1;
  }

  // Handle end of month edge cases
  const daysInEndMonth = new Date(cycleEndYear, cycleEndMonth, 0).getDate();
  if (cycleEndDay > daysInEndMonth) {
    cycleEndDay = daysInEndMonth;
  }

  const startDate = new Date(cycleStartYear, cycleStartMonth - 1, cycleStartDay);
  const endDate = new Date(cycleEndYear, cycleEndMonth - 1, cycleEndDay);

  // Set time to start of day
  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);

  const cycleKey = `${cycleStartYear}-${String(cycleStartMonth).padStart(2, '0')}-${String(cycleStartDay).padStart(2, '0')}`;

  return {
    startDate,
    endDate,
    cycleKey,
  };
};

/**
 * Check if a new cycle has started and needs to move previous cycle to history
 */
export const checkAndUpdateCycle = async (companyId?: string): Promise<{
  newCycleStarted: boolean;
  previousCycle?: SalaryCycle;
  currentCycle: SalaryCycle;
}> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  const config = await getSalaryCycleConfig(uid);
  if (!config) {
    throw new Error('Salary cycle configuration not found. Please set salary start date first.');
  }

  const now = new Date();
  const currentCycle = calculateCurrentCycle(now, config.salaryStartDate);

  // Check if we have a stored current cycle
  if (!config.currentCycleStartDate) {
    // First time, initialize
    await saveSalaryCycleConfig(config.salaryStartDate, uid);
    return {
      newCycleStarted: false,
      currentCycle,
    };
  }

  const storedCycleStart = new Date(config.currentCycleStartDate);
  storedCycleStart.setHours(0, 0, 0, 0);
  const newCycleStart = new Date(currentCycle.startDate);
  newCycleStart.setHours(0, 0, 0, 0);

  // Check if a new cycle has started
  if (newCycleStart.getTime() > storedCycleStart.getTime()) {
    // New cycle started, calculate previous cycle
    const previousCycle: SalaryCycle = {
      startDate: storedCycleStart,
      endDate: config.currentCycleEndDate ? new Date(config.currentCycleEndDate) : new Date(currentCycle.startDate.getTime() - 1),
      cycleKey: `${storedCycleStart.getFullYear()}-${String(storedCycleStart.getMonth() + 1).padStart(2, '0')}-${String(storedCycleStart.getDate()).padStart(2, '0')}`,
    };

    // Update config with new cycle
    await saveSalaryCycleConfig(config.salaryStartDate, uid);

    return {
      newCycleStarted: true,
      previousCycle,
      currentCycle,
    };
  }

  return {
    newCycleStarted: false,
    currentCycle,
  };
};

/**
 * Get all salary cycles for a guard (for history)
 */
export const getGuardSalaryCycles = async (
  guardId: string,
  companyId?: string
): Promise<Array<{
  cycleKey: string;
  startDate: Date;
  endDate: Date;
  data: any;
}>> => {
  const uid = companyId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  // This will be implemented to fetch cycles from guardAttendance/{guardId}/cycles
  // For now, return empty array
  return [];
};

